import React, { memo, useContext } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool } from 'prop-types'

import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { VIEWER_SIDEBAR_TAB } from 'Pods/ThreeDStudio/Store/constants'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import EnhanceModelPanel from './Components/EnhanceModelPanel/EnhanceModelPanel'
import RenderPreview from './Components/RenderPreview/RenderPreview'
import StylePanel from './Components/StylePanel/StylePanel'
import ViewerSidebarItem from './Components/ViewerSidebarItem/ViewerSidebarItem'
import useViewerSidebar from './useViewerSidebar'

function ViewerSidebar({ isModelOpened = false }) {
  const { enable2dRender } = useContext(ThreeDStudioPageContext)
  const {
    isOpen,
    navigationItems,
    isHistoryWindowOpen,
  } = useViewerSidebar(isModelOpened)

  const activeViewerSidebarTab = useThreeDStudioStore(state => state.ui?.activeViewerSidebarTab)
  const isStylePanelOpen = useThreeDStudioStore(state => state.ui?.isStylePanelOpen)
  // Rendered by the sidebar rather than by the Render tab so the flyout escapes the
  // enhance panel's clip (see __panels below), and so it still shows on mobile, where
  // the Enhance panel replaces .main-panel entirely.
  const isRenderTabActive = enable2dRender && activeViewerSidebarTab === VIEWER_SIDEBAR_TAB.RENDER
  const isStylePanelVisible = isRenderTabActive && isStylePanelOpen
  // The History tab, docked or detached, opens its finished renders in the same preview.
  const isRenderPreviewHosted = isRenderTabActive
    || (enable2dRender && (activeViewerSidebarTab === VIEWER_SIDEBAR_TAB.VARIATIONS || isHistoryWindowOpen))

  return (
    <div className="viewer-sidebar">
      {/* Clips only the sliding enhance panel, so an absolutely positioned flyout
          (StylePanel) can still overhang the sidebar's left edge. */}
      <div className="viewer-sidebar__panels">
        <EnhanceModelPanel isOpen={isOpen} isHistoryWindowOpen={isHistoryWindowOpen} />
        <nav
          className={classnames(
            'viewer-sidebar__nav',
            isOpen && 'viewer-sidebar__nav--opened',
          )}
          aria-label="viewer-sidebar"
        >
          {navigationItems.map(({
            id,
            ...rest
          }) => (
            <ViewerSidebarItem
              key={id}
              id={id}
              {...rest}
            />
          ))}
        </nav>
      </div>
      {/* Before StylePanel: both dock in the same slot, and picking a style covers the preview. */}
      {isRenderPreviewHosted && <RenderPreview isPanelOpen={isOpen} />}
      {isStylePanelVisible && <StylePanel />}
    </div>
  )
}

ViewerSidebar.propTypes = {
  isModelOpened: bool,
}

export default memo(ViewerSidebar)
